import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { RemoveButton } from "@/Common/Components/Buttons/RemoveButton";
import { CountrySelect } from "@/Common/Components/Contacts/CountrySelect";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { SelectKVPControl } from "@/Common/Components/Inputs/SelectKVPControl";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";

interface PhoneContactProps {
    phoneNumber: PhoneNumber;
    isRepresentativePhoneNumber?: boolean;
    autoFocusPhoneNumber?: boolean;
    onRemove: () => void;
    onPhoneTypeSelect: (value: string) => void;
    onCountrySelect: (val: string) => void;
    onPhoneNumberChange: (val: string) => void;
    onPhoneExtChange: (val: string) => void;
    onNoteChange: (val: string) => void;
    onIsPreferredChange: (val: boolean) => void;
    onDefaultNotificationChange: (val: boolean) => void;
}

class PhoneContactEdit extends BaseReactComponent<PhoneContactProps, null> {
    private readonly validationHelper = new ValidationHelper();

    public render(): JSX.Element {
        const phoneNumber = this.props.phoneNumber;

        const validationRule = this.validationHelper.getPhoneNumberValidationRule(phoneNumber.CountryLookupId, null);
        const mask = validationRule?.EditMaskPattern ?? "";
        const validationErrorMessage = validationRule?.ValidationErrorMessageLookupId
            ? cacheStore.GetLocalizedValueByLookupId(validationRule.ValidationErrorMessageLookupId)
            : "";

        const phoneLookupId = cacheStore.FindLookupByLookupCode("PHONE").LookupID;
        const workLookupId = cacheStore.FindLookupByLookupCode("WORK").LookupID;

        let hasExtensionField = (phoneLookupId == phoneNumber.PhoneTypeLookupId);
        if (this.props.isRepresentativePhoneNumber)
            hasExtensionField = hasExtensionField && (phoneNumber.ContactPointTypeLookupId == workLookupId);

        const extension = phoneNumber.Extension ?? "";

        return (
            <tbody>
                <tr>
                    <td className="c1">
                        <span className="float-right">{cacheStore.GetString("TypeLabel")}</span>
                    </td>
                    <td className="c2">
                        <SelectKVPControl
                            dataCy="phoneContact-type"
                            AvailableValues={this.getAvailablePhoneContactTypes()}
                            Value={this.phoneContactValue}
                            Callback={value => this.props.onPhoneTypeSelect(value)}
                        />
                    </td>
                    <td className="c3 with-line" rowSpan={this.props.isRepresentativePhoneNumber ? 5 : 4}>
                        <RemoveButton onRemove={() => this.props.onRemove()} />
                    </td>
                </tr>
                <tr>
                    <td className="c1">
                        <span className="float-right"> {cacheStore.GetString("CountryLabel")}</span>
                    </td>
                    <td className="c2">
                        <CountrySelect
                            dataCy="phoneContact-country"
                            onCountryChange={value => this.props.onCountrySelect(value)}
                            initialCountryLookupId={phoneNumber.CountryLookupId}
                        />
                    </td>
                </tr>
                <tr>
                    <td className="c1">
                        <span className="float-right">{cacheStore.GetString("NumberLabel")}</span>
                    </td>
                    <td className="c2">
                        <TextboxMaskedControl
                            dataCy="phoneContact-number"
                            isValid={phoneNumber.IsValid}
                            id={"phoneNumber" + phoneNumber.ContactPoint_id}
                            value={phoneNumber.Number}
                            item={phoneNumber}
                            mask={mask}
                            onChangeFunction={val => this.props.onPhoneNumberChange(val)}
                            validationErrorMessage={validationErrorMessage}
                            focus={this.props.autoFocusPhoneNumber}
                        />
                    </td>
                </tr>
                {hasExtensionField && (
                    <tr>
                        <td className="c1">
                            <span className="float-right"> {cacheStore.GetString("ExtLabel")}</span>
                        </td>
                        <td className="c2">
                            <TextboxMaskedControl
                                dataCy="phoneContact-ext"
                                id={"phoneExt" + phoneNumber.ContactPoint_id}
                                value={extension}
                                onChangeFunction={value => this.props.onPhoneExtChange(value)}
                                item={phoneNumber}
                                mask="[9{1,7}"
                                isValid
                            />
                        </td>
                    </tr>
                )}
                <tr>
                    <td className="c1">
                        <span className="float-right"> {cacheStore.GetString("NotesLabel")}</span>
                    </td>
                    <td className="c2">
                        <TextboxControl
                            dataCy="phoneContact-notes"
                            value={phoneNumber.Notes}
                            className="form-control"
                            onChange={(value: string) => this.props.onNoteChange(value)}
                            item={phoneNumber}
                        />
                    </td>
                </tr>
                {(!this.props.isRepresentativePhoneNumber || this.shouldRenderDefaultNotificationCheckbox()) && (
                    <tr>
                        <td className="c1">
                            <span className="float-right" />
                        </td>
                        <td className="c2">
                            {!this.props.isRepresentativePhoneNumber && (
                                <CheckboxControl
                                    dataCy="phoneContact-isPreferred"
                                    checked={phoneNumber.IsPreferred}
                                    label={cacheStore.GetString("PreferredPhoneDLabel")}
                                    onChange={checked => this.props.onIsPreferredChange(checked)}
                                    item={phoneNumber}
                                />
                            )}
                            {this.shouldRenderDefaultNotificationCheckbox() && (
                                <CheckboxControl
                                    style={{ marginLeft: !this.props.isRepresentativePhoneNumber ? "15px" : 0 }}
                                    checked={phoneNumber.NotificationsEnabled}
                                    label={cacheStore.GetString("NotifyPatientLabel")}
                                    onChange={checked => this.props.onDefaultNotificationChange(checked)}
                                    item={phoneNumber}
                                />
                            )}
                        </td>
                    </tr>
                )}
            </tbody>
        );
    }

    private getAvailablePhoneContactTypes(): KeyValuePair<string, string>[] {
        return cacheStore.GetPhoneContactPointTypeLookupTuples().map(pair => {
            const contactTypeId = pair[0].LookupID;
            const phoneTypeId = pair[1].LookupID;
            return new KeyValuePair(`${contactTypeId}_${phoneTypeId}`, `${cacheStore.GetLocalizedValueByLookupId(contactTypeId)} - ${cacheStore.GetLocalizedValueByLookupId(phoneTypeId)}`);
        });
    }

    private get phoneContactValue(): string {
        return `${this.props.phoneNumber.ContactPointTypeLookupId}_${this.props.phoneNumber.PhoneTypeLookupId}`;
    }

    private shouldRenderDefaultNotificationCheckbox(): boolean {
        return this.props.phoneNumber.PhoneTypeLookupId === cacheStore.getLookupByPhoneType(PhoneType.Mobile)?.LookupID;
    }
}

export { PhoneContactEdit };
